import styled, { keyframes, css, createGlobalStyle } from 'styled-components';
import { AT_COLORS, AT_FONTS } from '../../shared/design-system/tokens';

// ─── Global overrides ───────────────────────────────────────────────────────

export const ATHubGlobal = createGlobalStyle`
  html, body, #root { height: 100%; overflow: hidden; }
`;

// ─── Animations ─────────────────────────────────────────────────────────────

export const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.3; }
`;

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 12px var(--accent-glow); }
  50%       { box-shadow: 0 0 28px var(--accent-glow); }
`;

// ─── Shell ───────────────────────────────────────────────────────────────────

export const Root = styled.div`
  background: ${AT_COLORS.bg};
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: ${AT_COLORS.text};
  font-family: ${AT_FONTS.ui};
`;

// ─── TopBar ──────────────────────────────────────────────────────────────────

export const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  background: ${AT_COLORS.surface};
  border-bottom: 1px solid ${AT_COLORS.border};
  padding: 0 24px;
  height: 56px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const BackBtn = styled.button`
  background: transparent;
  border: 1px solid ${AT_COLORS.border};
  border-radius: 6px;
  padding: 5px 10px;
  color: ${AT_COLORS.textMuted};
  font-family: ${AT_FONTS.mono};
  font-size: 0.65rem;
  letter-spacing: 0.08em;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover {
    color: ${AT_COLORS.text};
    border-color: ${AT_COLORS.borderLight};
    background: ${AT_COLORS.surface2};
  }
`;

export const BrandDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${AT_COLORS.teal};
  box-shadow: 0 0 10px ${AT_COLORS.tealGlow};
  animation: ${pulse} 2s ease-in-out infinite;
  flex-shrink: 0;
`;

export const BrandName = styled.span`
  font-family: ${AT_FONTS.mono};
  font-size: 0.78rem;
  font-weight: 700;
  color: ${AT_COLORS.teal};
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

export const BrandSub = styled.span`
  font-family: ${AT_FONTS.mono};
  font-size: 0.6rem;
  color: ${AT_COLORS.textMuted};
  letter-spacing: 0.06em;
  margin-left: 2px;
`;

// ─── Hub content ─────────────────────────────────────────────────────────────

export const HubWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 48px 40px;
  animation: ${fadeUp} 0.3s ease both;
`;

export const HubLabel = styled.div`
  font-family: ${AT_FONTS.mono};
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${AT_COLORS.textDim};
  margin-bottom: 10px;
`;

export const HubTitle = styled.h1`
  font-family: ${AT_FONTS.mono};
  font-size: 1.5rem;
  font-weight: 700;
  color: ${AT_COLORS.text};
  letter-spacing: 0.04em;
  margin: 0 0 4px;
`;

export const HubSub = styled.p`
  font-size: 0.82rem;
  color: ${AT_COLORS.textMuted};
  margin: 0 0 36px;
`;

// ─── Cards grid ──────────────────────────────────────────────────────────────

export const CardsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 16px;
`;

export const ConsoleCard = styled.div<{ $accent: string }>`
  --accent: ${({ $accent }) => $accent};
  --accent-glow: ${({ $accent }) => $accent}44;

  position: relative;
  background: ${AT_COLORS.surface};
  border: 1px solid ${({ $accent }) => `${$accent}35`};
  border-radius: 12px;
  padding: 28px 28px 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
  cursor: default;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse at 0% 0%,
      ${({ $accent }) => `${$accent}18`} 0%,
      transparent 55%
    );
    pointer-events: none;
    border-radius: 12px;
  }

  &:hover {
    border-color: ${({ $accent }) => `${$accent}60`};
    box-shadow: 0 0 32px ${({ $accent }) => `${$accent}18`};
  }
`;

// ─── Card internals ──────────────────────────────────────────────────────────

export const LiveDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 6px ${({ $color }) => $color};
  animation: ${pulse} 1.4s ease-in-out infinite;
  flex-shrink: 0;
`;

export const CardStatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 20px;
`;

export const CardStatusText = styled.span`
  font-family: ${AT_FONTS.mono};
  font-size: 0.62rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${AT_COLORS.textMuted};
`;

export const CardMetric = styled.span<{ $accent: string }>`
  font-family: ${AT_FONTS.mono};
  font-size: 0.62rem;
  font-weight: 700;
  color: ${({ $accent }) => $accent};
  margin-left: 4px;
`;

export const ConsoleName = styled.h2<{ $accent: string }>`
  font-family: ${AT_FONTS.mono};
  font-size: 1.55rem;
  font-weight: 700;
  color: ${({ $accent }) => $accent};
  letter-spacing: 0.02em;
  margin: 0 0 6px;
  line-height: 1;
`;

export const ConsoleTagline = styled.p`
  font-size: 0.78rem;
  color: ${AT_COLORS.textMuted};
  line-height: 1.5;
  margin: 0 0 22px;
  max-width: 380px;
`;

export const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 28px;
  flex: 1;
`;

export const Feature = styled.div<{ $accent: string; $dim?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.76rem;
  color: ${({ $dim }) => ($dim ? AT_COLORS.textDim : AT_COLORS.text)};
  opacity: ${({ $dim }) => ($dim ? 0.5 : 1)};

  &::before {
    content: '';
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${({ $accent, $dim }) => ($dim ? AT_COLORS.textDim : $accent)};
    flex-shrink: 0;
  }
`;

export const FeatureName = styled.span<{ $accent: string; $dim?: boolean }>`
  color: ${({ $accent, $dim }) => ($dim ? AT_COLORS.textDim : $accent)};
  font-weight: 600;
`;

export const ComingSoonTag = styled.span`
  font-family: ${AT_FONTS.mono};
  font-size: 0.55rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${AT_COLORS.textDim};
  border: 1px solid ${AT_COLORS.border};
  border-radius: 99px;
  padding: 1px 6px;
  margin-left: 6px;
`;

export const EnterBtn = styled.button<{ $accent: string }>`
  --accent: ${({ $accent }) => $accent};
  --accent-glow: ${({ $accent }) => $accent}44;

  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-radius: 8px;
  border: 1px solid ${({ $accent }) => `${$accent}50`};
  background: ${({ $accent }) => `${$accent}14`};
  color: ${({ $accent }) => $accent};
  font-family: ${AT_FONTS.mono};
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.18s, border-color 0.18s, letter-spacing 0.18s;
  animation: ${css`${glow} 3s ease-in-out infinite`};

  &:hover:not(:disabled) {
    background: ${({ $accent }) => `${$accent}22`};
    border-color: ${({ $accent }) => $accent};
    letter-spacing: 0.14em;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    animation: none;
  }
`;

// ─── Coming soon strip ───────────────────────────────────────────────────────

export const ComingSoonStrip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: ${AT_COLORS.surface};
  border: 1px solid ${AT_COLORS.border};
  border-radius: 8px;
  opacity: 0.75;
`;

export const StripLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const StripIcon = styled.span`
  font-family: ${AT_FONTS.mono};
  font-size: 0.7rem;
  color: ${AT_COLORS.textMuted};
`;

export const StripName = styled.span`
  font-family: ${AT_FONTS.mono};
  font-size: 0.72rem;
  font-weight: 700;
  color: ${AT_COLORS.textMuted};
  letter-spacing: 0.06em;
`;

export const StripDesc = styled.span`
  font-size: 0.7rem;
  color: ${AT_COLORS.textMuted};
`;

export const StripBadge = styled.div`
  font-family: ${AT_FONTS.mono};
  font-size: 0.6rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${AT_COLORS.textMuted};
  border: 1px solid ${AT_COLORS.borderLight};
  border-radius: 99px;
  padding: 3px 10px;
`;
